import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { ButtonComponent } from '@app/components/common/button/button.component';
import { ProfileTypeDialogComponent } from '@app/components/registration/profile-type-dialog/profile-type-dialog.component';
import { LegalEntityDetails } from '@app/models/project.models';
import { RepresentationTypes } from '@app/models/shared.enums';
import { AuthService } from '@app/services/auth.service';
import { AuthStore } from '@app/stores/auth.store';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';

interface SelectableAccount {
  id: string;
  name: string;
  type: 'person' | 'legalEntity' | 'legalProfessional';
  suspended?: boolean;
  enabled?: boolean;
  stepInvestorQualification?: boolean | null;
}

@Component({
  selector: 'app-profile-selector',
  standalone: true,
  imports: [
    CommonModule,
    CdkOverlayOrigin,
    CdkConnectedOverlay,
    TranslateModule,
    ButtonComponent,
  ],
  templateUrl: './profile-selector.component.html',
  styleUrl: './profile-selector.component.scss',
})
export class ProfileSelectorComponent {
  protected open = signal(false);
  protected legalEntitySubAccounts = signal<LegalEntityDetails[]>([]);

  protected authStore = inject(AuthStore);
  private authService = inject(AuthService);
  private router = inject(Router);
  private modalService = inject(NgbModal);

  protected currentUserAsAccount = computed<SelectableAccount | null>(() => {
    const user = this.authStore.currentUser();
    if (!user) {
      return null;
    }
    return {
      id: user.id,
      name:
        user.personalData &&
        user.personalData.firstName &&
        user.personalData.lastName
          ? `${user.personalData.firstName} ${user.personalData.lastName}`.trim()
          : user.email,
      type: 'person',
    };
  });

  protected getCurrentCompany = computed<SelectableAccount | null>(() => {
    const id = this.authStore.loggedInCompanyId() ?? '';
    const name = this.authStore.loggedInCompanyName() ?? '';
    if (!id || !name) {
      return null;
    }
    const isProfessional = this.authStore.isUserQualifiedInvestor();
    return {
      id,
      name,
      type: isProfessional ? 'legalProfessional' : 'legalEntity',
    };
  });

  protected selected = signal<SelectableAccount | null>(
    this.authStore.loggedInCompanyId()
      ? this.getCurrentCompany()
      : this.currentUserAsAccount(),
  );

  protected filteredSubAccounts = computed(() => {
    const selectedId = this.selected()?.id;
    return this.legalEntitySubAccounts()
      .filter((acc) => acc.id !== selectedId)
      .map(
        (item) =>
          ({
            id: item.id,
            name: item.name,
            type:
              item.representation === 1 ? 'legalProfessional' : 'legalEntity',
            suspended: item.suspended,
            enabled: item.enabled,
            stepInvestorQualification: item.stepInvestorQualification,
          }) as SelectableAccount,
      );
  });

  constructor() {
    this.authStore
      .loadSubAccounts()
      .subscribe((response) => this.legalEntitySubAccounts.set(response.data));
  }

  getIconClassForDropdown(arg0: SelectableAccount | null) {
    if (!arg0) return 'fas fa-user';
    if (arg0.type === 'person') return 'fas fa-user';
    if (arg0.type === 'legalProfessional') return 'fas fa-hard-hat';
    return 'fas fa-building';
  }

  toggle(): void {
    this.open.update((o) => !o);
  }

  close(): void {
    this.open.set(false);
  }

  selectUser(): void {
    this.authStore.logoutAsCompany().subscribe(() => {
      this.selected.set(this.currentUserAsAccount());
      this.close();
      this.router.navigateByUrl('/profile/natural');
    });
  }

  selectSubAccount(option: SelectableAccount): void {
    this.authStore.loginAsCompany(option.id, true).subscribe({
      next: () => {
        this.selected.set(option);
        this.close();
      },
      complete: () => {
        if (window.location.pathname.includes('legal')) {
          window.location.reload();
        } else {
          this.router.navigateByUrl('/profile/legal');
        }
      },
    });
  }

  addNewSubAccount(): void {
    const modalRef = this.modalService.open(ProfileTypeDialogComponent, {
      centered: true,
    });

    modalRef.result.then((result: RepresentationTypes) => {
      switch (result) {
        case RepresentationTypes.PROFESSIONAL_CLIENT:
          this.router.navigate(['add-new-profile', 'professional']);
          return;
      }

      this.router.navigate(['add-new-profile', 'legal']);
      return;
    });
    this.close();
  }
}
